import { floorPlanVertexCenterCm } from '../perimeter/floor-plan-perimeter-vertex-geometry';
import type { UppFloorPlanVertex } from '../plan/floor-plan-element';
import type { UppFloorPlanTheme } from '../plan/floor-plan-theme';
import { floorPlanEditSelectedStrokeAttrs, floorPlanInnerStrokeAttrs } from './paint';

/**
 * Draggable marker for a **`VERTEX`** row: **circle** on the marker centre; edges use the same centre (see `floorPlanVertexCenterCm`).
 * In **view** mode (`edit === false`) the stroke/fill are invisible but pointer hit stays usable when `listening` is true.
 * In **edit** mode, **selected** uses the same **red** stroke as other edit-selected elements (`floorPlanEditSelectedStrokeAttrs`).
 */
export function createFloorPlanVertexNode(
  K: any,
  el: UppFloorPlanVertex,
  scalePxPerCm: number,
  theme: UppFloorPlanTheme,
  selected: boolean,
  listening: boolean,
  edit: boolean,
): any {
  const c = floorPlanVertexCenterCm(el);
  const cx = c.xCm * scalePxPerCm;
  const cy = c.yCm * scalePxPerCm;
  const r = (Math.min(el.widthCm, el.heightCm) / 2) * scalePxPerCm;
  /** ~25 % smaller markers than the model half-size so joints read cleaner next to thicker wall strokes. */
  const radiusPx = Math.max(2, r * 0.75);
  const show = edit;
  const strokeAttrs =
    show && selected
      ? floorPlanEditSelectedStrokeAttrs(theme)
      : show
        ? floorPlanInnerStrokeAttrs(theme, false)
        : { stroke: 'rgba(0,0,0,0)', strokeWidth: 0 };
  return new K.Circle({
    id: el.uuid,
    x: cx,
    y: cy,
    radius: radiusPx,
    fill: show ? theme.elementFill : 'rgba(0,0,0,0)',
    stroke: show ? strokeAttrs.stroke : 'rgba(0,0,0,0)',
    strokeWidth: show ? strokeAttrs.strokeWidth : 0,
    /** Wider invisible hit target in view mode so vertices stay selectable when the circle is hidden. */
    hitStrokeWidth: !show && listening ? Math.max(16, radiusPx * 2.5) : 0,
    listening,
  });
}
